import { CURRENT_JSON_VERSION, FormInstance, JsonStructure } from "db/definitions";
import { bulkSaveFieldInstances, createFieldForBatchResolver } from "db/queries/fieldInstanceQueries";
import { v4 as uuid } from "uuid";
import { _getFormGroupDocument } from "./_getFormGroupDocument";
import { handleImageObjCopy } from "./batchUploadFromPhotolog";
import { buildFullFormInstanceByFormInstanceId, bulkSaveFormInstances, createFormInstanceForBatchResolver } from "db/queries/formInstanceQueries";
import batchCreateFieldInstances from "./batchCreateFieldInstances";
import batchCreateImages from "./batchCreateImages";
import batchCreateFormInstances from "./batchCreateFormInstances";
import { bulkSaveImageDocs } from "db/queries/imageQueries";
import { handleBatchCrud } from "./handleBatchCrud";
import getFullFormInstanceForPhotolog from "./getFullFormInstanceForPhotolog";

export async function duplicateFormInstance(formInstance: FormInstance, online: boolean): Promise<FormInstance> {
	const final: JsonStructure = {
		projects: [],
		formGroups: [],
		formInstances: [],
		formTemplates: [],
		fieldInstances: [],
		revisions: [],
		fieldTemplates: [],
		options: [],
		continuationDatas: [],
		images: [],
		version: CURRENT_JSON_VERSION,
	};

	// The Form Instance being passed in contains all the form instance, field instance and image docs
	const fullFieldInstances = [];
	// Need to retrieve the most up to date full form instance first
	let fullFormInstance;
	if (online) {
		fullFormInstance = await getFullFormInstanceForPhotolog(formInstance.id, online);
	} else {
		fullFormInstance = await buildFullFormInstanceByFormInstanceId(formInstance.id);
	}

	if (!fullFormInstance) {
		throw new Error(`Could not retrieve full form instance for duplication with id: ${formInstance.id}`);
	}

	if (!fullFormInstance.formGroup) {
		fullFormInstance.formGroup = formInstance.formGroup;
	}
	// copy this so we get the id for the children copies.
	const formInstanceCopy = duplicate(fullFormInstance) as FormInstance;
	// need to loop through all the fieldInstances and duplicate those and add those into an array to be batch created
	const fieldInstances = fullFormInstance?.fieldInstances.items || [];
	for (let i = 0; i < fieldInstances.length; i++) {
		const fieldInstanceToCopy = fieldInstances[i];
		const fieldInstanceCopy = duplicate(fieldInstanceToCopy);
		// Need to handle image, altInstance and table fields if they exist
		if (fieldInstanceToCopy.imageId) {
			const image = fieldInstanceToCopy.image;
			if (image) {
				const imageCopy = duplicate(image);
				fieldInstanceCopy.imageId = imageCopy.id;
				const copiedKey = await handleImageObjCopy(imageCopy, true);
				imageCopy.key = copiedKey;
				final.images.push(imageCopy);
			}
		}

		if (fieldInstanceToCopy.tableFields) {
			// this is to map out all the ids, so I can setup the fields order afterwards
			// [original id]: newId
			const tableFieldIdMap: Record<string, string> = {};
			const fullTableFields = [];
			const newTableFieldIds: string[] = [];
			const newFieldsOrderArray = [];
			for (const tableField of fieldInstanceToCopy.tableFields.items) {
				const tableFieldCopy = duplicate(tableField);
				tableFieldIdMap[tableField.id] = tableFieldCopy.id;
				tableFieldCopy.tableField = fieldInstanceCopy.id;
				const image = tableFieldCopy.image;
				if (image) {
					const imageCopy = duplicate(image);
					tableFieldCopy.imageId = imageCopy.id;
					const copiedKey = await handleImageObjCopy(imageCopy, true);
					imageCopy.key = copiedKey;
					final.images.push(imageCopy);
				}
				const tableFieldForResolver = createFieldForBatchResolver(tableFieldCopy);
				newTableFieldIds.push(tableFieldCopy.id);
				final.fieldInstances.push(tableFieldForResolver);
				fullTableFields.push(tableFieldCopy);
			}
			if (fieldInstanceToCopy.fieldsOrder) {
				for (let i = 0; i < fieldInstanceToCopy.fieldsOrder.length; i++) {
					const rowOrder = fieldInstanceToCopy.fieldsOrder[i];
					const ids = rowOrder.split(",");
					let newRowOrder = "";
					for (const id of ids) {
						const newId = tableFieldIdMap[id];
						if (newId) newRowOrder += newRowOrder ? `,${newId}` : newId;
					}
					newFieldsOrderArray.push(newRowOrder);
				}
			}
			fieldInstanceCopy.tableFields = { items: fullTableFields };
			fieldInstanceCopy.fieldsOrder = newFieldsOrderArray;
		}

		// now for altInstance if it exists.
		if (fieldInstanceToCopy.altInstanceId) {
			const altInstance = fieldInstanceToCopy.altInstance;
			if (altInstance) {
				const altInstanceCopy = duplicate(altInstance);
				altInstanceCopy.formGroup = formInstanceCopy.formGroup;
				altInstanceCopy.formInstance = formInstanceCopy.id;
				altInstanceCopy.altInstanceId = altInstance.id;
				fieldInstanceCopy.altInstanceId = altInstanceCopy.id;
				const altInstanceForResolver = createFieldForBatchResolver(altInstanceCopy);
				final.fieldInstances.push(altInstanceForResolver);
			}
		}

		fieldInstanceCopy.formInstance = formInstanceCopy.id;
		const fieldInstanceForResolver = createFieldForBatchResolver(fieldInstanceCopy);

		fullFieldInstances.push(fieldInstanceCopy);
		final.fieldInstances.push(fieldInstanceForResolver);
	}
	formInstanceCopy.fieldInstances = { items: fullFieldInstances };
	final.formInstances.push(formInstanceCopy);
	const formInstanceForResolver = createFormInstanceForBatchResolver(formInstanceCopy);
	if (online) {
		try {
			// Now to create all the things
			const [fieldInstanceResult, imageResult, formInstanceResult] = await Promise.all([
				handleBatchCrud(final.fieldInstances, batchCreateFieldInstances, 25),
				handleBatchCrud(final.images, batchCreateImages, 25),
				handleBatchCrud([formInstanceForResolver], batchCreateFormInstances, 25),
			]);
			if (resultIsSuccessful(fieldInstanceResult) && resultIsSuccessful(imageResult) && resultIsSuccessful(formInstanceResult)) {
				return formInstanceCopy;
			} else {
				throw new Error(`Error duplicating form instance`);
			}
		} catch (error) {
			console.error("Error duplicating form instance:", error);
			throw error;
		}
	} else {
		await Promise.all([bulkSaveFieldInstances(final.fieldInstances), bulkSaveImageDocs(final.images), bulkSaveFormInstances([formInstanceForResolver])]);

		return formInstanceCopy;
	}
}

function resultIsSuccessful(result: boolean | boolean[]): boolean {
	if (Array.isArray(result)) {
		return !result.includes(false);
	} else {
		return result !== false;
	}
}

function duplicate<T extends { id?: string }>(document: T): T {
	const id = uuid();
	const copy = { ...document, id };
	return copy;
}
